/**
 * StakeInput Component - Phase 2C-2 (FIXED)
 * 
 * Input for entering stake amounts with preset buttons.
 * BSL Compliant: Factual display only, no risk indicators.
 * 
 * FIXED: Input onChange receives value string, not event
 */

import { Input, Button } from '../common';

interface StakeInputProps {
  value: number;
  onChange: (value: number) => void;
  maxBalance: number;
  disabled?: boolean;
  className?: string;
}

export function StakeInput({
  value,
  onChange,
  maxBalance,
  disabled = false,
  className = '',
}: StakeInputProps): JSX.Element {
  const presets = [
    { label: '10%', value: 0.1 },
    { label: '25%', value: 0.25 },
    { label: '50%', value: 0.5 },
    { label: '100%', value: 1.0 },
  ];

  // Input onChange receives the value string directly
  const handleInputChange = (valueStr: string) => {
    const numValue = parseFloat(valueStr);
    if (!isNaN(numValue) && numValue >= 0 && numValue <= maxBalance) {
      onChange(numValue);
    } else if (valueStr === '') {
      onChange(0);
    }
  };

  const handlePresetClick = (percentage: number) => {
    const amount = Math.floor(maxBalance * percentage);
    onChange(amount);
  };

  const balanceAfter = maxBalance - value;

  return (
    <div className={className}>
      <label className="block text-sm font-medium text-slate-300 mb-2">
        Stake Amount
      </label>
      
      {/* Preset Buttons */}
      <div className="flex gap-2 mb-3">
        {presets.map((preset) => (
          <Button
            key={preset.label}
            variant="secondary"
            size="sm"
            disabled={disabled}
            onClick={() => handlePresetClick(preset.value)}
          >
            {preset.label}
          </Button>
        ))}
      </div>

      {/* Input */}
      <Input
        type="number"
        value={value.toString()}
        onChange={handleInputChange}
        disabled={disabled}
        placeholder="Enter stake amount"
      />

      {/* Balance Info */}
      <div className="mt-3 space-y-1 text-sm">
        <div className="flex justify-between text-slate-400">
          <span>Available Balance</span>
          <span className="font-mono">{maxBalance.toLocaleString()}</span>
        </div>
        <div className="flex justify-between text-slate-400">
          <span>Balance After Stake</span>
          <span className="font-mono">{balanceAfter.toLocaleString()}</span>
        </div>
      </div>
    </div>
  );
}
